import React, { useMemo, useState } from 'react'
import { Box, Grid, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import DatePicker from 'components/DatePicker'
import Button from 'components/Button'
import KPICards from './Components/KPICards'
import RecentDocuments from './Components/RecentDocuments'
import CommentsByCategory from './Components/CommentsByCategory'
import DocumentStatusOverview from './Components/DocumentStatusOverview'
import UpcomingDeadlines from './Components/UpcomingDeadlines'

const USER_ID_FILTER_ROLES = [
    'Subcommittee Secretary',
    'Subcommittee Member',
    'Committee Secretary',
    'Committee Member',
    'Subcommittee Chair'
]

const NO_USER_ID_ROLES = [
    'Admin',
    'National Codex Contact Point',
    'National Codex Committee Chair',
    'National Codex Committee Secretary',
    'National Codex Committee Member',
]

const normalizeRole = (role = '') => String(role).trim().replace(/\s+/g, ' ').toLowerCase()

const shouldIncludeUserId = (allowedRoles = [], userRoles = []) => {
    const normalizedAllowedRoles = allowedRoles.map(normalizeRole)
    const normalizedUserRoles = userRoles.map((role) => normalizeRole(role?.name || role))

    return normalizedUserRoles.some((role) => normalizedAllowedRoles.includes(role))
}

const getDefaultDateRange = () => {
    const now = new Date()
    const year = now.getFullYear()
    const month = now.getMonth()

    return {
        fromDate: new Date(year, month, 1),
        toDate: now,
    }
}

const getFiltersFromDateRange = (dateRange = {}) => {
    const fromDate = dateRange?.fromDate
    const toDate = dateRange?.toDate

    if (!(fromDate instanceof Date) || Number.isNaN(fromDate.getTime())) {
        return { is_active: true }
    }

    if (!(toDate instanceof Date) || Number.isNaN(toDate.getTime())) {
        return { is_active: true }
    }

    const pad = (n) => String(n).padStart(2, '0')
    const fromDateIso = `${fromDate.getFullYear()}-${pad(fromDate.getMonth() + 1)}-${pad(fromDate.getDate())}`
    const toDateIso = `${toDate.getFullYear()}-${pad(toDate.getMonth() + 1)}-${pad(toDate.getDate())}`

    return {
        from_date: fromDateIso,
        to_date: toDateIso,
        is_active: true,
    }
}

const getCommitteeId = (user = {}) => {
    if (user?.committee_id) return user.committee_id
    if (user?.committee?.id) return user.committee.id

    const committeeMembership = (user?.committeeMemberships || user?.committee_memberships || []).find(
        (item) => item?.committee_id || item?.committee?.id
    )

    return committeeMembership?.committee_id || committeeMembership?.committee?.id || null
}

const Dashboard = () => {
    const theme = useTheme()
    const today = useMemo(() => new Date(), [])
    const user = useSelector((state) => state.auth.user)
    const [dateRange, setDateRange] = useState(getDefaultDateRange)
    const [filters, setFilters] = useState(() => getFiltersFromDateRange(getDefaultDateRange()))
    const activeUser = user || {}
    const userRoles = activeUser?.roles || []

    const normalizedUserRoles = useMemo(
        () => userRoles.map((role) => role?.name || role),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [activeUser?.id]
    )

    const dashboardFilters = useMemo(() => {
        const baseFilters = { ...filters }

        const committeeId = getCommitteeId(activeUser)
        if (committeeId) {
            baseFilters.committee_id = committeeId
        }

        if (
            shouldIncludeUserId(USER_ID_FILTER_ROLES, normalizedUserRoles) &&
            !shouldIncludeUserId(NO_USER_ID_ROLES, normalizedUserRoles) &&
            activeUser?.id
        ) {
            baseFilters.user_id = activeUser.id
        }

        const primaryRole = normalizedUserRoles[0] || ''
        if (primaryRole) {
            baseFilters.role = primaryRole
        }

        return baseFilters
    }, [activeUser?.id, filters, normalizedUserRoles])

    const displayName = useMemo(() => {
        const name = activeUser?.name || activeUser?.full_name || ''
        const trimmedName = name.trim()
        if (!trimmedName) return 'Dr.'
        return trimmedName
    }, [activeUser])

    const { monthDayText, yearText } = useMemo(() => {
        const now = new Date()
        const month = now.toLocaleString('en-US', { month: 'short' })
        const day = String(now.getDate()).padStart(2, '0')
        return {
            monthDayText: `${month} ${day}`,
            yearText: String(now.getFullYear()),
        }
    }, [])

    const applyDateFilters = () => {
        if (!dateRange?.fromDate || !dateRange?.toDate) {
            toast.error('Please select both From Date and To Date.')
            return
        }

        if (dateRange.fromDate > dateRange.toDate) {
            toast.error('From Date cannot be later than To Date.')
            return
        }

        setFilters(getFiltersFromDateRange(dateRange))
    }

    return (
        <Box className="w-full" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
                sx={{
                    borderRadius: 3,
                    p: { xs: 2, sm: 2.5, md: 3 },
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 20%, #113B78 80%)`,
                    boxShadow: theme.shadows[2],
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'space-between',
                    gap: 2,
                }}
            >
                <Box>
                    <Typography
                        variant="h4"
                        sx={{
                            color: '#FFFFFF',
                            fontWeight: 700,
                            lineHeight: 1.2,
                            fontSize: { xs: '0.85rem', sm: '1rem', md: '1.5rem' },
                            mb: 0.75,
                        }}
                    >
                        {`Welcome back, ${displayName}`}
                    </Typography>
                    <Typography
                        sx={{
                            color: 'rgba(255, 255, 255, 0.82)',
                            fontSize: { xs: '0.46rem', sm: '0.64rem', md: '0.875rem' },
                        }}
                    >
                        National Codex Contact Point · Ministry of Health
                    </Typography>
                </Box>

                <Box
                    sx={{
                        alignSelf: { xs: 'flex-end', sm: 'auto' },
                        minWidth: { xs: 'auto', sm: 108 },
                        px: 2,
                        py: 1.5,
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.12)',
                        //border: '1px solid rgba(255, 255, 255, 0.15)',
                        textAlign: 'center',
                    }}
                >
                    <Typography sx={{ color: theme.palette.sidebar?.text, fontSize: { xs: '0.95rem', sm: '1.25rem' }, fontWeight: 700, lineHeight: 1.05 }}>
                        {monthDayText}
                    </Typography>
                    <Typography sx={{ color: '#9FC6FF', fontSize: { xs: '0.46rem', sm: '0.64rem', md: '0.75rem' }, fontWeight: 600 }}>
                        {yearText}
                    </Typography>
                </Box>
            </Box>

           
                <Grid container spacing={1.5} alignItems="flex-end">
                    <Grid size={{ lg: 5, md: 4, sm: 6, xs: 12 }}>
                        <DatePicker
                            name="from_date"
                            label="From Date"
                            value={dateRange.fromDate}
                            maxDate={today}
                            onChange={(value) => {
                                setDateRange((prev) => ({ ...prev, fromDate: value || null }))
                            }}
                        />
                    </Grid>

                    <Grid size={{ lg: 5, md: 4, sm: 6, xs: 12 }}>
                        <DatePicker
                            name="to_date"
                            label="To Date"
                            value={dateRange.toDate}
                            minDate={dateRange.fromDate || undefined}
                            maxDate={today}
                            onChange={(value) => {
                                setDateRange((prev) => ({ ...prev, toDate: value || null }))
                            }}
                        />
                    </Grid>

                    <Grid size={{ lg: 2, md: 4, sm: 12, xs: 12 }}>
                        <Button
                            fullWidth
                            onClick={applyDateFilters}
                            //className="btn-success"
                            sx={{ height: 40 }}
                        >
                            Filter
                        </Button>
                    </Grid>
                </Grid>
          

            <Grid container spacing={2} alignItems="stretch">
                <Grid size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>
                    <KPICards filters={dashboardFilters} userRoles={normalizedUserRoles} />
                </Grid>

                <Grid size={{ lg: 8, md: 6, sm: 12, xs: 12 }}>
                    <RecentDocuments />
                </Grid>

                <Grid size={{ lg: 4, md: 6, sm: 12, xs: 12 }}>
                    <CommentsByCategory filters={dashboardFilters} />
                </Grid>

                <Grid size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
                    <DocumentStatusOverview filters={dashboardFilters} />
                </Grid>

                <Grid size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
                    <UpcomingDeadlines userRoles={normalizedUserRoles} role={normalizedUserRoles[0] || ''} />
                </Grid>
            </Grid>

            
        </Box>
    )
}

export default Dashboard;
